import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withRouterLegacy } from 'core/routerUtils';
import { withTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
import * as tcEventActions from 'features/tc-event-dashboard/duck';
import * as roomActions from 'features/room-builder/duck';
import * as staffActions from 'features/staff-and-rooms/duck';
import * as staticActions from 'shared/ducks/staticDuck';
import * as taskActions from 'features/task-management/duck';
import { mapRoomsByLocationWithCapacity } from 'features/tc-event-dashboard/selectors';
import {
  StaffCard,
  StaffList,
  RoomStaff,
  AssignedCountsCapacities,
  CreateRoom,
  NotifyStaff,
  RoomDetailsModal,
  StaffPool,
  AssignStaff,
  AssignNonPersonnelStaff,
} from 'features/staff-and-rooms';
import * as staffHelpers from 'features/staff-and-rooms/helpers';
import { PermissionsService } from 'shared/services';
import { ROOM_STATUS } from 'shared/constants';
import {
  AlertMessage,
  AlertNotice,
  SimpleModal,
  Spinner,
  EventDashboardDetails,
  PageNotFound,
} from 'shared/components';
import {
  getEventStaffGroups,
  getStaffByRoom,
  getNonPersonnelStaff,
  getTechnicalCoordinator,
} from 'features/staff-and-rooms/selectors';
import { getPersonnelRoles } from 'shared/selectors';
import { getPersonnelRolesName, getRoleById } from 'shared/helpers';
import BuildingIcon from 'assets/svgs/buildingIcon.svg';

import './StaffAndRoomsContainer.scss';
import TaskManagementService from '../../task-management/TaskManagementService';

const getSeats = (rooms = []) => rooms.reduce((acc, { capacity }) => acc + (capacity || 0), 0);

class StaffAndRoomsContainer extends Component {
  constructor(props) {
    super(props);

    this.state = {
      roomModalOpen: false,
      assignStaffModalOpen: false,
      assignNonPersonnelStaffModalOpen: false,
      currentRoom: {},
      deleteDisabled: false,
      deleteModalOpen: false,
      showAlertMessage: false,
      alertMessage: '',
      currentStaff: null,
      currentRole: null,
      notifyStaffOpen: false,
      removeRoleModalOpen: false,
      roomDeleteStatus: {},
    };

    this.personnelRolesName = getPersonnelRolesName();
  }

  componentDidMount() {
    const { params } = this.props.match;
    this.props.tcEventActions.getTcEvent(params.eventId).then(() => {
      this.props.tcEventActions.getTestCenterCycle(params.eventId);
      if (!isEmpty(this.props.tcEvent)) {
        this.props.staticActions.getCapacityTypes(
          this.props.tcEvent.testingEvent.adminId.cycleId.id
        );
        this.props.roomActions.getRoomOptions(this.props.tcEvent.testingEvent.adminId.cycleId.id);
      }
      this.props.tcEventActions.getTestCenterCycle(params.eventId).then(() => {
        if (!isEmpty(this.props.testCenterCycle)) {
          this.props.staffActions.getTcNonPersonnelStaff(
            this.props.testCenterCycle.testCenterProgram.testCenter.id
          );
          this.checkRoomsDeleteStatusIfNeeded();
        }
      });
    });
    this.props.tcEventActions.getRooms(params.eventId);
    this.props.staffActions.getTcEventStaff(params.eventId);
    this.props.staffActions.getTcEventNonPersonnelStaff(params.eventId);

    this.props.staffActions.getAvailableTcStaff(params.eventId, {
      page: 0,
      limit: 200,
    });
    this.props.staffActions.getInvitedEventStaff(params.eventId);

    if (this.props.location.state?.successNotif) {
      this.setState({
        showAlertMessage: true,
        showMessageTime: 3000,
        isErrorAlertMessage: false,
        alertMessage: this.props.location.state.successNotif,
      });
      globalThis.history.replaceState(null, ''); // Remove message on page reload
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.rooms.length !== this.props.rooms.length) {
      this.checkRoomsDeleteStatusIfNeeded();
    }

    if (
      this.props.location.state?.successNotif &&
      prevProps.location.state?.successNotif !== this.props.location.state.successNotif
    ) {
      this.setState({
        showAlertMessage: true,
        showMessageTime: 3000,
        isErrorAlertMessage: false,
        alertMessage: this.props.location.state.successNotif,
      });
      globalThis.history.replaceState(null, ''); // Remove message on page reload
    }
  }

  checkRoomsDeleteStatusIfNeeded = () => {
    const { testCenterCycle, rooms } = this.props;
    // Only check delete status if seating chart is NOT required and we have rooms
    if (testCenterCycle?.cycle?.seatingChartRequiredFlag === false && rooms && rooms.length > 0) {
      this.checkAllRoomsDeleteStatus();
    }
  };

  checkAllRoomsDeleteStatus = async () => {
    const { rooms } = this.props;
    const roomDeleteStatus = {};

    const statusPromises = rooms.map(async (room) => {
      try {
        const roomStatus = await TaskManagementService.getRoomStatus(room.id);

        const isDeleteDisabled =
          roomStatus &&
          !!(
            roomStatus.submittedIrregularityCount ||
            roomStatus.lastCompletedTaskId ||
            roomStatus.status !== ROOM_STATUS.inactive
          );

        roomDeleteStatus[room.id] = isDeleteDisabled;
      } catch (error) {
        roomDeleteStatus[room.id] = false;
      }
    });

    await Promise.all(statusPromises);

    this.setState({ roomDeleteStatus });
  };

  renderTechnicalCoordinator = () => {
    const { roles, tcEventNonPersonnelStaff, personnelRoles } = this.props;
    const techCoordId = staffHelpers.getRoleId(roles, personnelRoles.TECHNICAL_COORDINATOR);
    return (
      <>
        <div className="delimiter" />
        <StaffCard
          staff={tcEventNonPersonnelStaff}
          roleId={techCoordId}
          title={this.personnelRolesName.TECHNICAL_COORDINATOR}
          onOpenModal={this.toggleAssignNonPersonnelStaff}
          showContactInfo
        />
      </>
    );
  };

  renderRovingProctors = () => {
    const { rooms, roles, staffGroups, personnelRoles, tcEvent, testCenterCycle } = this.props;

    const unlimitedFlag =
      tcEvent.unlimitedRovingProctorsFlag ||
      (testCenterCycle.cycle && testCenterCycle.cycle.unlimitedRovingProctorsFlag);
    const nrOfProctors = Math.floor(rooms.length / 8);
    const rovingProctorId = staffHelpers.getRoleId(roles, personnelRoles.PROCTOR_ROVING);
    const { rovingProctors } = staffGroups;
    const countProctors = unlimitedFlag ? rovingProctors.length : nrOfProctors;
    return (
      (!!nrOfProctors || unlimitedFlag) && (
        <>
          <div className="delimiter" />
          {!unlimitedFlag && (
            <AlertNotice message={this.props.t('staffManagement.rovingProctorMessage')} />
          )}
          <StaffList
            list={[
              ...rovingProctors,
              ...Array.from({ length: countProctors - rovingProctors.length }, () => ({})),
            ]}
            roleTitle={this.personnelRolesName.PROCTOR_ROVING}
            roleId={rovingProctorId}
            onOpenModal={this.toggleAssignStaff}
            onRemoveStaffRole={this.onUnassignStaff}
            showDelete={unlimitedFlag}
            addLabel="staffManagement.addRovinProctorLabel"
          />
        </>
      )
    );
  };

  copyRoomsSuccess = () => {
    const { tcEvent } = this.props;

    this.props.tcEventActions.getRooms(tcEvent.id);
    this.setState({
      showAlertMessage: true,
      alertMessage: 'roomBuilder.copyRoomsSuccess',
    });
  };

  onDeleteRoom = (deleteRoomFlag, showDeleteModal) => {
    const { currentRoom } = this.state;
    const { tcEvent } = this.props;

    if (showDeleteModal || !deleteRoomFlag) {
      this.setState({
        deleteModalOpen: showDeleteModal,
      });
      return;
    }

    this.props.tcEventActions.deleteRoom(currentRoom.id).then(() => {
      if (!this.props.errorDeleteRoom) {
        this.props.tcEventActions.deleteRoomAction({ roomId: currentRoom.id });
        this.props.staffActions.getTcEventStaff(tcEvent.id);
        this.props.staffActions.getAvailableTcStaff(tcEvent.id, {
          page: 0,
          limit: 200,
        });
        this.setState({
          currentRoom: {},
          showAlertMessage: true,
          alertMessage: 'roomBuilder.deleteRoomSuccess',
          deleteModalOpen: showDeleteModal,
          roomModalOpen: false,
          errorDeleteRoom: '',
        });
        return;
      }
      this.setState({
        errorDeleteRoom: this.props.errorDeleteRoom,
      });
    });
  };

  onDirectDeleteRoom = (roomId) => {
    const { rooms } = this.props;
    const room = rooms.find((r) => r.id === roomId);
    if (room) {
      this.setState(
        {
          currentRoom: room,
        },
        () => {
          // Call the existing onDeleteRoom method to show the modal
          this.onDeleteRoom(false, true);
        }
      );
    }
  };

  toggleNotifyStaff = () => {
    this.setState((prevState) => ({
      notifyStaffOpen: !prevState.notifyStaffOpen,
    }));
  };

  handleSubmitNotifyModal = (hasError) => {
    this.setState({
      notifyStaffOpen: false,
      showAlertMessage: true,
      showMessageTime: 3000,
      isErrorAlertMessage: hasError,
      alertMessage: hasError
        ? 'staffManagement.notifyStaff.warningMessage'
        : 'staffManagement.notifyStaff.successMessage',
    });
  };

  renderRooms = () => {
    const { params } = this.props.match;
    const { locations, tcEvent, testCenterCycle, rooms, staffByRooms } = this.props;
    const { roomDeleteStatus } = this.state;
    const unlimitedFlag =
      tcEvent.unlimitedRoomProctorsFlag ||
      (testCenterCycle.cycle && testCenterCycle.cycle.unlimitedRoomProctorsFlag);
    const { seatingChartRequiredFlag } = testCenterCycle.cycle || {};

    return !!locations.length && !!rooms.length ? (
      <div className="rooms-section">
        {locations.map((location, index) => (
          <div className="border-box-radius" key={`location-${location.id}`}>
            <div className="border-box-header">
              <div>
                <BuildingIcon />
                <h5>
                  {location.name}
                  {location.area && location.area.length > 0 && `, ${location.area}`}{' '}
                  <span className="rooms-and-seats">
                    ({location.rooms?.length || 0} rooms | {getSeats(location.rooms)} seats)
                  </span>
                </h5>
              </div>
            </div>
            <div className="delimiter" />
            {location.rooms && location.rooms.length && (
              <RoomStaff
                roles={this.props.roles}
                eventId={+params.eventId}
                rooms={location.rooms}
                staffByRooms={staffByRooms}
                onOpenModal={this.toggleAssignStaff}
                onRemoveStaffRole={this.onUnassignStaff}
                unlimitedRoomProctorsFlag={unlimitedFlag}
                openRoomModal={this.toggleRoomModal}
                onEditRoom={(roomId) => this.goToRoomBuilder(roomId)}
                onDeleteRoom={(roomId) => this.onDirectDeleteRoom(roomId)}
                seatingChartRequiredFlag={seatingChartRequiredFlag}
                roomDeleteStatus={roomDeleteStatus}
                startPosition={index === 0 ? 0 : null}
                mappedRoomTiming={this.props.mappedRoomTiming}
              />
            )}
          </div>
        ))}
      </div>
    ) : (
      <CreateRoom
        testCenterCycle={testCenterCycle}
        tcEvent={tcEvent}
        locations={locations}
        onCreateRoom={() => this.goToRoomBuilder()}
        copyRoomsSuccess={this.copyRoomsSuccess}
      />
    );
  };

  goToRoomBuilder = (roomId) => {
    const { history, tcEvent } = this.props;

    history.push(`/test-event/${tcEvent.id}/rooms/information${roomId ? `/${roomId}` : ''}`);
  };

  toggleRoomModal = (room) => {
    this.setDeleteStatus(room);
    this.setState((prevState) => ({
      roomModalOpen: !prevState.roomModalOpen,
      currentRoom: room
        ? {
            ...room,
          }
        : {},
    }));
  };

  setDeleteStatus = (room) => {
    if (!room) {
      this.props.taskActions.getRoomStatusAction({});
      this.setState({
        deleteDisabled: false,
      });
      return;
    }

    this.props.taskActions.getRoomStatus(room.id).then(() => {
      const { roomStatus } = this.props;
      if (
        roomStatus &&
        (roomStatus.submittedIrregularityCount ||
          roomStatus.lastCompletedTaskId ||
          roomStatus.status !== ROOM_STATUS.inactive)
      ) {
        this.setState({
          deleteDisabled: true,
        });
        return;
      }

      this.setState({
        deleteDisabled: false,
      });
    });
  };

  removeNotifMessage = () => {
    this.setState({
      showAlertMessage: false,
    });
  };

  toggleAssignStaff = (staff, roleId, room) => {
    const role = getRoleById(roleId);

    this.setState({
      assignStaffModalOpen: !this.state.assignStaffModalOpen,
      currentStaff: staff,
      currentRole: role,
      currentRoom: room,
    });
  };

  toggleAssignNonPersonnelStaff = (staff, roleId) => {
    const role = getRoleById(roleId);
    this.setState({
      assignNonPersonnelStaffModalOpen: !this.state.assignNonPersonnelStaffModalOpen,
      currentStaff: staff,
      currentRole: role,
    });
  };

  closeNonPersonnelStaffModal = () => {
    this.setState({
      assignNonPersonnelStaffModalOpen: false,
    });
  };

  handleAssignNonPersonnel = async (testcenterEventId, nonPersonnelId, role) => {
    await this.props.staffActions.assignEventNonPersonnelStaff(
      testcenterEventId,
      nonPersonnelId,
      role
    );
    if (!this.props.error) {
      this.setState({
        assignNonPersonnelStaffModalOpen: false,
      });
    }
  };

  handleUnAssignNonPersonnel = async (testcenterEventId, nonPersonnelId, role) => {
    await this.props.staffActions.unAssignEventNonPersonnelStaff(
      testcenterEventId,
      nonPersonnelId,
      role
    );
    if (!this.props.error) {
      this.setState({
        assignNonPersonnelStaffModalOpen: false,
      });
    }
  };

  handleAddNonPersonnel = async (nonPersonnel) => {
    const { testCenterCycle } = this.props;
    await this.props.staffActions.createTcNonPersonnelStaff(
      testCenterCycle.testCenterProgram.testCenter.id,
      nonPersonnel
    );
  };

  fetchData = () => {
    this.props.staffActions.getTcEventStaffAction({ data: [] });
    this.props.staffActions.getAvailableTcStaffAction({ data: [] });
    this.props.staffActions.getTcEventStaff(this.props.tcEvent.id);
    this.props.staffActions.getAvailableTcStaff(this.props.tcEvent.id, {
      page: 0,
      limit: 200,
    });
  };

  editAssignStaff = async (staff, role, roomId, assignmultipersonnel, currentAssignedStaff) => {
    const data = {
      personnelId: staff.personnelId,
      testCenterEventId: this.props.tcEvent.id,
      roleId: role.id,
      roomId,
      assignmultipersonnel,
      id: currentAssignedStaff.testCenterEventPersonnelId,
    };
    if (staff.testCenterEventPersonnelId && staff.personnelId && !assignmultipersonnel) {
      await this.props.staffActions.updateEventStaff({
        id: staff.testCenterEventPersonnelId,
        ...data,
      });
    } else {
      await this.props.staffActions.assignEventStaff(data);
    }
  };

  toggleRemoveRoleModal = (modalOpen, staff) => {
    this.setState({
      removeRoleModalOpen: modalOpen,
      currentStaff: staff,
    });
  };

  onUnassignStaff = async (staff, unlimitedRoleFlag) => {
    if (unlimitedRoleFlag) {
      this.toggleRemoveRoleModal(true, staff);
    } else if (!isEmpty(staff)) {
      this.toggleRemoveRoleModal(false, null);
      await this.props.staffActions.unassignEventStaff(staff.testCenterEventPersonnelId);
      this.fetchData();
    }
  };

  onUpdateStaff = async (
    staff,
    currentAssignedStaff,
    role,
    roomId,
    unassignFlag,
    omitConfirm,
    multiAssign,
    sameRoomMultiAssign
  ) => {
    if (unassignFlag && staff && !multiAssign && isEmpty(currentAssignedStaff)) {
      await this.onUnassignStaff(staff);
      this.setState({
        assignStaffModalOpen: false,
      });
      return;
    }

    if (unassignFlag && !isEmpty(currentAssignedStaff)) {
      await this.props.staffActions.unassignEventStaff(
        currentAssignedStaff.testCenterEventPersonnelId
      );
    }
    if ((multiAssign && roomId === staff.roomId) || sameRoomMultiAssign) {
      await this.onUnassignStaff(sameRoomMultiAssign || staff);
    }
    if (!unassignFlag) {
      await this.editAssignStaff(staff, role, roomId, multiAssign, currentAssignedStaff);
    }

    this.fetchData();
    this.setState({
      assignStaffModalOpen: false,
    });
  };

  render() {
    const {
      tcEvent,
      testCenterCycle,
      staffGroups,
      personnelRoles,
      roles,
      locations,
      capacityTypes,
      rooms,
      roomOptions,
      currentUser,
      tcNonPersonnelStaff,
      tcEventNonPersonnelStaff,
      error,
      loading,
    } = this.props;
    const { currentStaff, currentRole, currentRoom, removeRoleModalOpen } = this.state;

    const anyRoomOnline = rooms.some((room) => room.roomType && room.roomType.onlineFlag);

    if ((!roles.length && isEmpty(tcEvent)) || isEmpty(testCenterCycle)) {
      return <Spinner />;
    }

    if (
      !PermissionsService.hasAccessTo('testCenterEventRoomManage', 'testEvents', tcEvent.id) &&
      !PermissionsService.hasAccessTo(
        'testCenterEventRoomManage',
        'testCenters',
        testCenterCycle.id
      )
    ) {
      return <PageNotFound />;
    }

    return (
      <section className="container staff-and-rooms-container">
        <div className="position-relative top-section">
          <h4 className="">{this.props.t('staffAndRoomsManagement.title')}</h4>
          <EventDashboardDetails
            date={tcEvent.testingEvent.name}
            name={
              testCenterCycle.testCenterProgram.testCenter &&
              testCenterCycle.testCenterProgram.testCenter.name
            }
          />
        </div>
        <div className="staff-and-rooms-main-section">
          <div className="left-column">
            <div className="border-box-radius sitewide-staff">
              <h5>{this.props.t('staffAndRoomsManagement.sitewideSectionTitle')}</h5>
              {!testCenterCycle.testCenterProgram.testCenter.nowManagedFlag && (
                <>
                  <div className="delimiter" />
                  <StaffCard
                    staff={staffGroups.tdCoordinator}
                    roleId={staffHelpers.getRoleId(roles, personnelRoles.TEST_DAY_COORDINATOR)}
                    showEdit={PermissionsService.hasTestCenterManage(testCenterCycle.id)}
                    title={this.personnelRolesName.TEST_DAY_COORDINATOR}
                    onOpenModal={this.toggleAssignStaff}
                  />
                  {anyRoomOnline && this.renderTechnicalCoordinator()}
                </>
              )}
              {this.renderRovingProctors()}
            </div>
            {!!rooms.length && (
              <div className="create-room-button-container">
                <button
                  className="btn btn-small btn-action ml-0 w-100"
                  onClick={() => this.goToRoomBuilder()}>
                  {this.props.t('staffAndRoomsManagement.createRoomLabel')}
                </button>
              </div>
            )}
            {this.renderRooms()}
          </div>
          <div className="right-column">
            {!testCenterCycle.testCenterProgram.testCenter.nowManagedFlag && (
              <AssignedCountsCapacities
                tcEvent={tcEvent}
                locations={locations}
                capacityTypes={capacityTypes}
                createRoomFlag={!!rooms.length}
              />
            )}
            <StaffPool
              staffGroups={staffGroups}
              tcEvent={tcEvent}
              nowManagedFlag={testCenterCycle.testCenterProgram.testCenter.nowManagedFlag}
              openNotifyModal={this.toggleNotifyStaff}
              testCenterCycle={testCenterCycle}
              currentUserId={currentUser.id}
              staffActions={this.props.staffActions}
            />
          </div>
          <RoomDetailsModal
            room={this.state.currentRoom}
            modalOpen={this.state.roomModalOpen}
            deleteDisabled={this.state.deleteDisabled}
            toggleModal={this.toggleRoomModal}
            onDeleteRoom={this.onDeleteRoom}
            onEditRoom={(roomId) => this.goToRoomBuilder(roomId)}
            roomOptions={roomOptions}
            eventDetails={{
              tcCycleName: testCenterCycle.testCenterProgram.testCenter.name,
              testingEventName: tcEvent.testingEvent.name,
            }}
            mappedRoomTiming={this.props.mappedRoomTiming}
          />
          <AssignStaff
            assignableStaffList={staffGroups.assignableStaff}
            currentAssignedStaff={currentStaff}
            currentRole={currentRole}
            currentRoom={currentRoom}
            eventStaff={this.props.eventStaff}
            modalOpen={this.state.assignStaffModalOpen}
            testCenterCycle={testCenterCycle}
            toggleModal={this.toggleAssignStaff}
            onUpdateStaff={this.onUpdateStaff}
            tcEvent={tcEvent}
            mappedRoomTiming={this.props.mappedRoomTiming}
          />
          <AssignNonPersonnelStaff
            nonPersonnelStaffList={tcNonPersonnelStaff}
            currentAssignedStaff={tcEventNonPersonnelStaff}
            currentRole={currentRole}
            modalOpen={this.state.assignNonPersonnelStaffModalOpen}
            toggleModal={this.closeNonPersonnelStaffModal}
            tcEvent={tcEvent}
            testCenterCycle={testCenterCycle}
            handleAssign={this.handleAssignNonPersonnel}
            handleUnAssign={this.handleUnAssignNonPersonnel}
            handleAddNonPersonnel={this.handleAddNonPersonnel}
            error={error}
            loading={loading}
          />
          <SimpleModal
            containerClassName="modal-delete-room"
            isOpen={this.state.deleteModalOpen}
            onCancel={() => this.onDeleteRoom(false, false)}
            onConfirm={() => this.onDeleteRoom(true, false)}
            confirmLabel={this.props.t('common.labels.delete')}
            cancelLabel={this.props.t('common.labels.cancel')}
            headline={this.props.t('roomBuilder.deleteRoomQuestion')}>
            <div>
              <p>{this.props.t('roomBuilder.deleteRoomHeader')}</p>
              {this.state.errorDeleteRoom && (
                <AlertNotice message={this.props.t('errors.saveError')} type="error" />
              )}
            </div>
          </SimpleModal>
          <SimpleModal
            isOpen={removeRoleModalOpen}
            onConfirm={() => this.onUnassignStaff(currentStaff, false)}
            onCancel={() => this.toggleRemoveRoleModal(false, null)}
            confirmLabel={this.props.t('common.labels.remove')}
            cancelLabel={this.props.t('common.labels.cancel')}>
            <div>
              <h4>{this.props.t('staffAndRoomsManagement.removeRoleModal.title')}</h4>
              <p>
                {this.props.t('staffAndRoomsManagement.removeRoleModal.message', {
                  name:
                    currentStaff && !isEmpty(currentStaff)
                      ? currentStaff.firstName.concat(currentStaff.lastName, ' ')
                      : '',
                })}
              </p>
            </div>
          </SimpleModal>
          <AlertMessage
            show={this.state.showAlertMessage}
            removeMessage={this.removeNotifMessage}
            message={this.state.alertMessage}
            time={this.state.showMessageTime}
            isError={this.state.isErrorAlertMessage}
          />
        </div>
        <button className="btn btn-outline" onClick={this.props.history.goBack}>
          {this.props.t('common.labels.back')}
        </button>
        <NotifyStaff
          modalIsOpen={this.state.notifyStaffOpen}
          closeModal={this.toggleNotifyStaff}
          handleSubmitNotifyModal={this.handleSubmitNotifyModal}
          staffGroups={staffGroups}
          testCenterCycle={testCenterCycle}
          tcEvent={tcEvent}
          currentUser={currentUser}
        />
      </section>
    );
  }
}

function mapStateToProps(state) {
  const { testCenterCycle, currentEvent, rooms } = state.tcEvent;

  return {
    testCenterCycle: testCenterCycle || {},
    tcEvent: currentEvent || {},
    rooms: rooms || [],
    locations: mapRoomsByLocationWithCapacity(state),
    eventStaff: state.staff.tcEventStaff || [],
    staffGroups: getEventStaffGroups(state),
    staffByRooms: getStaffByRoom(state),
    availableTcStaff: state.staff.availableTcStaff,
    roles: state.statics.roles,
    currentUser: state.users.currentUser,
    personnelRoles: getPersonnelRoles(state),
    capacityTypes: state.statics.capacityTypes,
    roomOptions: state.roomOptions.options,
    errorDeleteRoom: state.tcEvent.errorDeleteRoom,
    roomStatus: state.taskManagement.roomStatus,
    mappedRoomTiming: state.statics.mappedRoomTiming,
    tcNonPersonnelStaff: getNonPersonnelStaff(state),
    tcEventNonPersonnelStaff: getTechnicalCoordinator(state),
    error: state.staff.error,
    loading: state.staff.loading,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    tcEventActions: bindActionCreators(tcEventActions, dispatch),
    roomActions: bindActionCreators(roomActions, dispatch),
    staffActions: bindActionCreators(staffActions, dispatch),
    staticActions: bindActionCreators(staticActions, dispatch),
    taskActions: bindActionCreators(taskActions, dispatch),
  };
}

export default withTranslation()(
  withRouterLegacy(connect(mapStateToProps, mapDispatchToProps)(StaffAndRoomsContainer))
);

StaffAndRoomsContainer.propTypes = {
  t: PropTypes.func.isRequired,
  roomStatus: PropTypes.shape({}),
  match: PropTypes.shape({
    params: PropTypes.shape({}),
  }).isRequired,
  testCenterCycle: PropTypes.shape({}).isRequired,
  tcEvent: PropTypes.shape({}).isRequired,
  staffActions: PropTypes.shape({
    getTcEventStaff: PropTypes.func,
  }).isRequired,
  tcEventActions: PropTypes.shape({
    getTestCenterCycle: PropTypes.func,
    getTcEvent: PropTypes.func,
    getRooms: PropTypes.func,
  }).isRequired,
  staticActions: PropTypes.shape({
    getRoles: PropTypes.func,
  }).isRequired,
  taskActions: PropTypes.shape({
    getRoomStatus: PropTypes.func.isRequired,
    getRoomStatusAction: PropTypes.func.isRequired,
  }).isRequired,
  rooms: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  locations: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  roles: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  tcNonPersonnelStaff: PropTypes.arrayOf(PropTypes.shape({})),
  tcEventNonPersonnelStaff: PropTypes.shape({}),
};
